import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GoogleLogo } from '@phosphor-icons/react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSelector } from '../components/LanguageSelector';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/auth/login`, formData);
      const { token, ...userData } = response.data;
      login(userData, token);
      toast.success(t('loginSuccess'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('loginError'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Redirect to auth callback page that will handle the session exchange
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background grain-overlay flex items-center justify-center p-6">
      {/* Language Selector - top right */}
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-3 mb-12" data-testid="logo-link">
          <img src="/logo.png" alt="SharpBetAI" className="h-20 w-auto" />
        </Link>

        {/* Login Card */}
        <div className="glass-surface p-8 rounded-sm" data-testid="login-form">
          <h1 className="text-3xl font-heading font-bold text-white mb-2">{t('welcomeBack')}</h1>
          <p className="text-white/50 mb-8">{t('enterAccount')}</p>

          {/* Google Login */}
          <button
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 px-6 py-3 border border-white/20 rounded-full text-white font-semibold hover:border-white/40 hover:bg-white/5 transition-all duration-150 mb-6"
            data-testid="google-login-button"
          >
            <GoogleLogo weight="bold" className="text-xl" />
            {t('continueWithGoogle')}
          </button>

          <div className="relative flex items-center justify-center mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10" />
            </div>
            <div className="relative px-4 bg-accent text-white/40 text-sm">{t('or')}</div>
          </div>

          {/* Email Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">{t('email')}</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 rounded-sm text-white placeholder:text-white/30 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all duration-150"
                placeholder="your@email.com"
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">{t('password')}</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 rounded-sm text-white placeholder:text-white/30 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all duration-150"
                placeholder="••••••••"
                required
                data-testid="password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3.5 bg-primary text-background font-black rounded-full hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 shadow-lg hover:shadow-primary/20"
              data-testid="login-submit-button"
            >
              {loading ? '...' : t('enter')}
            </button>
          </form>

          <p className="text-center text-white/50 mt-6">
            {t('noAccount')}{' '}
            <Link to="/signup" className="text-primary font-semibold hover:text-primary/80" data-testid="signup-link">
              {t('createAccount')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
